"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"
import Image from "next/image"
import { useLocale, useTranslations } from "next-intl"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Store, Ticket, Tag, Package, BadgeEuro, type LucideIcon } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { GlassCard } from "@/components/glass-card"
import DonistaLogo from "@/components/icons/donista-logo"
import { Link } from "@/i18n/navigation"
import { DonistaClickLink } from "@/components/donista-click-link"
import { usePopularShops } from "@/app/admin/shops/_api/use-shop-queries"
import { useDiscountCodes } from "@/hooks/queries"
import { useAppContext } from "@/contexts/app-context"
import { useHeroProducts, type HeroProductView } from "./use-hero-products"
import { formatCurrencyCompact, formatNumber } from "@/lib/utils/currency"
import { formatShortDate } from "@/lib/utils/date"
import { maskCode } from "@/lib/utils/mask-code"
import { CardStackAuto, ProductTile, ShopRateBadge } from "./hero"
import { MENU_ITEM_STYLES, paletteColor, tint } from "./hero-styles"
import { PRODUCT_RANGE_SHOPS_PARAMS, PRODUCT_RANGE_CODES_PARAMS } from "./product-range-constants"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"

type Locale = "de" | "en"

function EmptyState({ message }: { message: string }) {
  return <p className="py-10 text-center text-muted-foreground">{message}</p>
}

/* ======================================================================== */
/* showcase — horizontal menu + reused hero card stacks                    */
/* ======================================================================== */

function StackSkeleton() {
  return <Skeleton className="mx-auto h-[380px] w-full max-w-[240px] md:max-w-[320px] rounded-2xl" />
}

/**
 * The exact hero "Card stack auto" geometry, reused for every showcase face:
 * `CardStackAuto` in horizontal mode with a narrow width and a fixed `h-[380px]` card.
 */
function ShowcaseStack<T>({ items, renderCard }: {
  items: T[]
  renderCard: (item: T, isFront: boolean) => ReactNode
}) {
  // Horizontal fan: the two background cards spread out left/right (instead of a flat vertical
  // pile) so the stack uses the width and reads as more dynamic. Only ShowcaseStack opts in —
  // the hero right-column stack keeps CardStackAuto's vertical defaults.
  // On mobile the card + fan shrink so the side cards don't bleed off a narrow screen (the
  // parent <section> also `overflow-x-clip`s as a hard backstop against horizontal scroll).
  const isMobile = useIsMobile()
  return (
    <CardStackAuto
      items={items}
      renderCard={renderCard}
      horizontal
      xSpread={isMobile ? 40 : 150}
      tilt={isMobile ? 4 : 6}
      className="mx-auto w-full max-w-[240px] md:max-w-[320px]"
      stackClassName="relative h-[380px] overflow-visible"
    />
  )
}

/**
 * Shared FIXED card height so Shops / Codes / Produkte are all identically sized — switching tabs
 * causes no height jump. Each card is a `flex flex-col` (media `h-44` on top, badge pinned to the
 * bottom via `mt-auto`), giving a consistent portrait card across the three stacks. One tunable spot.
 */
const STACK_CARD_HEIGHT = "h-[380px]"

/* ----- stack faces ----- */

function ShopsStack({ locale }: { locale: Locale }) {
  const t = useTranslations("start.productRange")
  const { data, isLoading } = usePopularShops(PRODUCT_RANGE_SHOPS_PARAMS)
  const shops = data?.content ?? []
  if (isLoading) return <StackSkeleton />
  if (shops.length === 0) return <EmptyState message={t("empty")} />
  const card = (s: (typeof shops)[number]) => (
    <GlassCard className={cn(STACK_CARD_HEIGHT, "flex flex-col")}>
      {/* Mirrors the Produkte card: same full-width h-44 white media window on top, content below.
          Logo is capped so it always occupies the same space, the rest of the box stays white. */}
      <div className="flex h-44 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
        {s.logoUrl ? (
          <Image src={s.logoUrl} alt={s.name} width={176} height={176} className="max-h-24 w-auto max-w-[75%] object-contain" />
        ) : (
          <span className="text-4xl font-semibold text-muted-foreground">{s.name.charAt(0)}</span>
        )}
      </div>
      <p className="mt-4 truncate text-center text-lg font-semibold">{s.name}</p>
      <div className="mt-auto pt-1.5">
        <ShopRateBadge block summary={s.commissionRangeSummary} shopId={s.id} locale={locale} />
      </div>
    </GlassCard>
  )
  return (
    <ShowcaseStack
      items={shops}
      renderCard={(s, isFront) =>
        isFront ? (
          <Link href={`/shops/${s.slug}`} aria-label={s.name} className="block">{card(s)}</Link>
        ) : (
          <div className="pointer-events-none">{card(s)}</div>
        )
      }
    />
  )
}

/* ----- vouchers (static placeholder — upcoming feature, display only) ----- */

/** Static placeholder gift-card vouchers (upcoming feature — display only). */
interface PlaceholderVoucher {
  shop: string
  /** Voucher face value in EUR. */
  value: number
  /** Donation generated when buying the voucher (placeholder). */
  donation: number
  /** Mono voucher code shown on the gift card. */
  code: string
  /** Brand hex color for the card face gradient. */
  color: string
}

const PLACEHOLDER_VOUCHERS: PlaceholderVoucher[] = [
  { shop: "Zalando", value: 50, donation: 3.0, code: "DON-ZAL-50", color: "#008009" },
  { shop: "IKEA", value: 100, donation: 6.0, code: "DON-IKE-100", color: "#af1e2d" },
  { shop: "Douglas", value: 25, donation: 1.5, code: "DON-DOU-25", color: "#0071e3" },
  { shop: "MediaMarkt", value: 75, donation: 4.5, code: "DON-MM-75", color: "#008009" },
]

function VoucherCard({ voucher, locale, featured = false }: { voucher: PlaceholderVoucher; locale: string; featured?: boolean }) {
  const t = useTranslations("start.hero")
  const color = voucher.color
  return (
    <div
      className={`relative aspect-[856/540] overflow-hidden rounded-2xl border border-white/20 text-white shadow-xl ${featured ? "p-7" : "p-5"}`}
      style={{ background: `linear-gradient(to bottom right, ${color}, ${color}D9, ${color}B3)` }}
    >
      <span className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
      <span className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-background" />
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 rounded-md bg-white px-1.5 py-1">
          <DonistaLogo className="h-4 w-4" />
          <span className="text-xs font-bold text-foreground">donista</span>
        </span>
        <span className="font-mono text-xs tracking-widest opacity-90">{voucher.code}</span>
      </div>
      <p className={`mt-6 font-extrabold ${featured ? "text-5xl" : "text-3xl"}`}>{formatCurrencyCompact(voucher.value, locale)}</p>
      <p className="text-sm opacity-90">{voucher.shop}</p>
      <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1 text-sm font-semibold backdrop-blur-sm">
        <BadgeEuro className="h-4 w-4" />
        {formatNumber(voucher.donation, locale)} {t("donation")}
      </div>
    </div>
  )
}

function VoucherStack({ locale }: { locale: string }) {
  const reduce = useReducedMotion()
  const allVouchers = PLACEHOLDER_VOUCHERS
  const [index, setIndex] = useState(0)
  const hovered = useRef(false)
  useEffect(() => {
    if (reduce || allVouchers.length <= 1) return
    const id = setInterval(() => {
      if (!hovered.current) setIndex((p) => (p + 1) % allVouchers.length)
    }, 5000)
    return () => clearInterval(id)
  }, [reduce, allVouchers.length])
  const stack = [0, 1, 2].map((o) => allVouchers[(index + o) % allVouchers.length]!)
  const tilts = [-5, 0, 5]

  return (
    <div
      className="mx-auto max-w-[340px]"
      onMouseEnter={() => { hovered.current = true }}
      onMouseLeave={() => { hovered.current = false }}
    >
      <div className="relative min-h-[240px]">
        {stack.map((v, si) => (
          <motion.div
            key={`${index}-${si}`}
            className={si === 0 ? "absolute inset-x-0 cursor-pointer" : "absolute inset-x-0 pointer-events-none"}
            style={{ zIndex: 3 - si }}
            animate={{ y: si * 16, rotate: tilts[si], scale: 1 - si * 0.04, opacity: 1 - si * 0.25 }}
            whileHover={si === 0 ? { scale: 1.04 } : undefined}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <VoucherCard voucher={v} locale={locale} />
          </motion.div>
        ))}
      </div>
      <div className="mt-4 flex justify-center gap-1.5">
        {allVouchers.map((_, idx) => (
          <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === index % allVouchers.length ? "w-6 bg-primary" : "w-1.5 bg-primary/30"}`} />
        ))}
      </div>
    </div>
  )
}

function VouchersStack({ locale }: { locale: Locale }) {
  // Badge sits a hair left of the voucher column center (`left-[48%]`) to seat on the front
  // card's raised (left) edge, tilted -5° to sit parallel to it (resting tilt `tilts[0] === -5`).
  const t = useTranslations("start.productRange")
  return (
    <div className="relative pt-3">
      <span className="absolute left-[48%] top-2 z-10 -translate-x-1/2 -rotate-[5deg] rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-primary-foreground shadow">
        {t("comingSoon")}
      </span>
      <VoucherStack locale={locale} />
    </div>
  )
}

function CodesStack({ locale }: { locale: Locale }) {
  const t = useTranslations("start.productRange")
  // Logged-in users (incl. anonymous Firebase accounts) see the real code; logged-out visitors and
  // crawlers (null user) get the masked form. Client-only decision — the query is never SSR-prefetched,
  // so full codes never land in the crawlable DOM. Mirrors shop-discount-codes-scroll.tsx's `revealed`.
  const { user } = useAppContext()
  const revealed = !!user
  const { data, isLoading } = useDiscountCodes(PRODUCT_RANGE_CODES_PARAMS)
  const codes = data?.content ?? []
  if (isLoading) return <StackSkeleton />
  if (codes.length === 0) return <EmptyState message={t("empty")} />
  const card = (code: (typeof codes)[number]) => {
    const discount = code.discountPercentage
      ? `-${formatNumber(code.discountPercentage, locale)} %`
      : code.discountAmount
        ? `-${formatCurrencyCompact(code.discountAmount, locale)}`
        : null
    return (
      <GlassCard className={cn(STACK_CARD_HEIGHT, "flex flex-col")}>
        {/* Mirrors the Produkte card: same full-width h-44 white media window on top, content below.
            Logo is capped so it always occupies the same space, the rest of the box stays white. */}
        <div className="flex h-44 w-full shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">
          {code.shopLogoUrl ? (
            <Image src={code.shopLogoUrl} alt={code.shopName ?? ""} width={176} height={176} className="max-h-24 w-auto max-w-[75%] object-contain" />
          ) : (
            <span className="text-4xl font-semibold text-muted-foreground">{(code.shopName ?? code.title ?? "?").charAt(0)}</span>
          )}
        </div>
        <p className="mt-4 truncate text-center text-lg font-semibold">{code.title ?? code.shopName}</p>
        {/* Shop name under the title, mirroring the Produkte card — only when a distinct title exists
            (otherwise the title already IS the shop name and we'd duplicate it). */}
        {code.title && code.shopName ? (
          <p className="truncate text-center text-xs text-muted-foreground">{code.shopName}</p>
        ) : null}
        {discount ? <p className="mt-1 text-3xl font-extrabold text-primary">{discount}</p> : null}
        {/* Code chip pinned to the bottom, full-width centered — mirrors the badge slot in Shops/Produkte. */}
        <div className="mt-auto pt-1.5">
          {code.code ? (
            <span className="block w-full truncate rounded-lg bg-muted px-2 py-1.5 text-center font-mono text-sm tracking-widest">{revealed ? code.code : maskCode(code.code)}</span>
          ) : null}
          {code.endsAt ? (
            <p className="mt-1 text-center text-xs text-muted-foreground">
              {t("ends")} {formatShortDate(code.endsAt, locale)}
            </p>
          ) : null}
        </div>
      </GlassCard>
    )
  }
  return (
    <ShowcaseStack
      items={codes}
      renderCard={(code, isFront) =>
        isFront ? (
          <Link href={code.shopSlug ? `/shops/${code.shopSlug}` : "/shops"} className="block">{card(code)}</Link>
        ) : (
          <div className="pointer-events-none">{card(code)}</div>
        )
      }
    />
  )
}

function ProductsStack({ locale }: { locale: Locale }) {
  const t = useTranslations("start.productRange")
  const { products, isLoading } = useHeroProducts()
  if (isLoading) return <StackSkeleton />
  if (products.length === 0) return <EmptyState message={t("empty")} />
  return (
    <ShowcaseStack
      items={products}
      renderCard={(p: HeroProductView, isFront) => {
        const tile = (
          <ProductTile
            product={p}
            locale={locale}
            className={`${STACK_CARD_HEIGHT} ${isFront ? "transition-shadow hover:shadow-xl" : ""}`}
          />
        )
        return isFront ? (
          p.targetUrl ? (
            // Tracked affiliate redirect to the real product/shop (opens in a new tab). Falls back
            // to the internal shops route when no affiliate link was captured for this product.
            <DonistaClickLink href={p.targetUrl} className="block cursor-pointer">{tile}</DonistaClickLink>
          ) : (
            <Link href={p.shopSlug ? `/shops/${p.shopSlug}` : "/shops"} className="block cursor-pointer">{tile}</Link>
          )
        ) : (
          <div className="pointer-events-none">{tile}</div>
        )
      }}
    />
  )
}

/* ----- menu + composite ----- */

type CategoryKey = "shops" | "vouchers" | "codes" | "products"

const CATEGORY_ICONS: Record<CategoryKey, LucideIcon> = {
  shops: Store,
  vouchers: Ticket,
  codes: Tag,
  products: Package,
}

const CATEGORY_ORDER: CategoryKey[] = ["shops", "vouchers", "codes", "products"]

function ShowcaseFace({ category, locale }: { category: CategoryKey; locale: Locale }) {
  switch (category) {
    case "vouchers":
      return <VouchersStack locale={locale} />
    case "codes":
      return <CodesStack locale={locale} />
    case "products":
      return <ProductsStack locale={locale} />
    default:
      return <ShopsStack locale={locale} />
  }
}

export function ProductRangeShowcase() {
  const locale = (useLocale() === "de" ? "de" : "en") as Locale
  const reduce = useReducedMotion()
  const t = useTranslations("start.productRange")
  const [active, setActive] = useState<CategoryKey>("shops")
  const menuStyle = MENU_ITEM_STYLES.default!
  const menuColor = paletteColor("logoRed")

  // Pre-warm the discount-codes query on page load so the "Codes" tab isn't a cold
  // client fetch when selected (mirrors the SSR-warmed "Shops" tab). Same query key as
  // CodesStack → deduped to one request. Result intentionally unused here.
  useDiscountCodes(PRODUCT_RANGE_CODES_PARAMS)

  return (
    <section className="overflow-x-clip py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t("title")}</h2>
            <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{t("subtitle")}</p>
          </div>

          {/* Horizontal menu — the logo-red accent tints ONLY the active button
              (idle ones stay theme green). */}
          <div className="grid grid-cols-2 gap-3 md:flex md:flex-wrap md:items-center md:justify-center">
            {CATEGORY_ORDER.map((key) => {
              const Icon = CATEGORY_ICONS[key]
              const isActive = key === active
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActive(key)}
                  aria-pressed={isActive}
                  style={isActive ? tint(menuColor) : undefined}
                  className={cn(
                    "flex items-center justify-start gap-2.5 border px-4 py-2.5 text-left transition-colors md:shrink-0",
                    menuStyle,
                    isActive ? "border-primary bg-primary/10" : "border-border/40 bg-background/40 hover:border-primary/50",
                  )}
                >
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold">{t(`tabs.${key}`)}</span>
                </button>
              )
            })}
          </div>

          {/* Centered showcase */}
          <div className="mx-auto mt-12 flex min-h-[380px] max-w-xl items-center justify-center overflow-visible">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                className="w-full overflow-visible"
                initial={reduce ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -12 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              >
                <ShowcaseFace category={active} locale={locale} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}
